<template>
    <div id="wallet-settings">
        <WalletAddBalance v-if="addBalanceModal" @add-balance="addBalanceWhatsapp" @close="addBalanceModal = false" />
        <CreditCardNeeded @close="creditCardNeededModal = false" v-if="creditCardNeededModal"/>
        <AutoRebalancePromoModal 
            v-if="showAutoRebalancePromo" 
            @activate="handleAutoRebalancePromoActivate" 
            @close="handleAutoRebalancePromoClose" 
        />
        <div class="wallet">
            <div class="whatsapp-wallet">
                <div class="logo">
                    <WalletSVG />
                </div>
                <div class="content">
                    <div class="wallet-info">
                        <div class="title">{{ $t("billing.availableBalance") }}</div>
                        <div class="balance" v-if="wallet.whatsapp">{{ formatPrice(wallet.whatsapp.amount) }}TL</div>
                    </div>
                    <div class="wallet-add" @click="openAddWhatsappBalanceModal">{{ $t("billing.addBalance") }}</div>
                </div>
            </div>
        </div>

        <!-- Main Content Tabs -->
        <div class="wallet-content">
            <div class="wallet-tabs">
                <div class="tab-buttons">
                    <div 
                        class="tab-button"
                        :class="{ active: activeMainTab === 'transactions' }"
                        @click="setActiveMainTab('transactions')"
                    >
                        <span class="material-icons">receipt_long</span>
                        Cüzdan Hareketleri
                    </div>
                    <div 
                        class="tab-button"
                        :class="{ active: activeMainTab === 'auto-rebalance' }"
                        @click="setActiveMainTab('auto-rebalance')"
                    >
                        <span class="material-icons">autorenew</span>
                        Otomatik Yükleme
                    </div>
                </div>
            </div>

            <!-- Transactions Tab Content -->
            <div v-if="activeMainTab === 'transactions'" class="tab-content transactions-content">
                <div class="wallet-logs">
            <div class="product-tabs">
                <div class="tab-buttons">
                    <div 
                        v-for="tab in productTabs" 
                        :key="tab.key"
                        class="tab-button"
                        :class="{ active: activeTab === tab.key }"
                        @click="setActiveTab(tab.key)"
                    >
                        <span class="material-icons">{{ tab.icon }}</span>
                        {{ tab.label }}
                    </div>
                </div>
                <div class="type-chips" v-if="currentTabTypes.length > 0">
                    <div class="chips-label">
                        İşlem Tipleri: 
                        <span class="chips-hint">(Tıklayarak tek seçim, Ctrl+tık ile çoklu seçim)</span>
                    </div>
                    <div class="chips-container">
                        <div 
                            v-for="typeKey in currentTabTypes" 
                            :key="typeKey"
                            class="type-chip"
                            :class="{ active: selectedTypes.includes(typeKey) }"
                            @click="toggleType(typeKey, $event)"
                        >
                            {{ types[typeKey] }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="title">
                {{ $t("billing.walletLogs") }}
                <div class="pagination">
                    <div>
                        <a-date-picker 
                            v-model="filters.startDate" 
                            :placeholder="$t('message.startDate')" 
                            show-time 
                            format="YYYY-MM-DD HH:mm:ss"
                        />
                        <a-date-picker 
                            v-model="filters.endDate" 
                            :placeholder="$t('message.endDate')" 
                            show-time 
                            format="YYYY-MM-DD HH:mm:ss"
                        />
                    </div>
                    <a-select v-if="activeTab === 'whatsapp-business-api'" v-model="filters.channelId" class="filters-type">
                        <a-select-option v-for="channel of channels" :value="channel._id" :key="channel._id">
                            {{ channel.name }}
                        </a-select-option>
                        <a-select-option :selected="true" :value="''"
                            >{{ $t("message.selectChannel") }}
                        </a-select-option>
                    </a-select>
                    <div class="pagination-controls">
                        <div class="text">
                            {{ filters.perPage * (filters.page - 1) + 1 }} -
                            {{ Math.min(filters.perPage * filters.page, totalActivity) }} / {{ totalActivity }}
                            {{ $t("message.records") || "kayıt" }}
                        </div>
                        <div class="page-controls">
                            <a-button class="pagination-button" @click="backwardPage" :disabled="filters.page <= 1">
                                <div class="material-icons">arrow_back_ios</div>
                            </a-button>
                            <a-input-number 
                                v-model="currentPageInput" 
                                :min="1" 
                                :max="totalPages"
                                @pressEnter="jumpToPage"
                                @blur="jumpToPage"
                                class="page-input"
                            />
                            <span class="page-separator">/ {{ totalPages }}</span>
                            <a-button class="pagination-button" @click="forwardPage" :disabled="filters.page >= totalPages">
                                <div class="material-icons">arrow_forward_ios</div>
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="logs">
                <a-table :columns="columns" :data-source="activities" :pagination="false">
                    <p slot="createdAt" slot-scope="record" style="margin: 0">{{ formatDate(record) }}</p>
                    <p slot="type" slot-scope="record, item" style="margin: 0">
                        {{ formatType(record, item) }}
                        <span v-if="item.type === 'ai-usage'">
                            ({{ getAIUsageResource(item) }})
                        </span>
                    </p>
                    <p slot="phoneNumber" slot-scope="record, item" style="margin: 0">
                        {{ getPhoneNumber(item) }}
                    </p>
                    <p slot="country" slot-scope="record, item" style="margin: 0">
                        {{ $_.get(item, "payload.country", "-") }}
                    </p>
                    <p slot="before" slot-scope="record, item" style="margin: 0">
                        <MoneyFormatter :price="item.before.toFixed(4)"></MoneyFormatter>
                    </p>
                    <p slot="after" slot-scope="record, item" style="margin: 0">
                        <MoneyFormatter :price="item.after.toFixed(4)"></MoneyFormatter>
                    </p>
                    <p slot="amount" slot-scope="record, item" style="margin: 0">
                        <MoneyFormatter :price="item.amount.toFixed(4)"></MoneyFormatter>
                    </p>
                </a-table>
            </div>
                </div>
            </div>

            <!-- Auto Rebalance Tab Content -->
            <div v-if="activeMainTab === 'auto-rebalance'" class="tab-content auto-rebalance-content">
                <!-- Bilgilendirici Alert'ler -->
                <div class="info-alerts">
                    <div class="alert-item compact warning">
                        <span class="material-icons">info</span>
                        <div class="alert-content">
                            <div class="alert-title">Önemli Bilgiler</div>
                            <div class="alert-points">
                                <div class="point">
                                    <span class="bullet">⚠️</span>
                                    <span>Kartınızdan ödeme alınamazsa sistem otomatik pasife geçer, kendiniz aktif etmelisiniz.</span>
                                </div>
                                <div class="point">
                                    <span class="bullet">⏰</span>
                                    <span>10 dakikada sadece 1 kez yükleme yapar. Yoğun kullanım için hedef bakiyeyi yüksek tutun.</span>
                                </div>
                                <div class="point">
                                    <span class="bullet">🔔</span>
                                    <span>Daha fazla bildirim için <router-link to="/settings/notifications" class="link">Bildirim Ayarları</router-link>'ndan açabilirsiniz.</span>
                                </div>
                                <div class="point">
                                    <span class="bullet">📋</span>
                                    <span>Otomatik yüklemeleri "Cüzdan Hareketleri" sekmesinde görebilirsiniz.</span>
                                </div>
                                <div class="point">
                                    <span class="bullet">∞</span>
                                    <span>Yükleme sayısında ve miktarında limitasyon yoktur, doğru ayarları girdiğinizden emin olun.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="auto-rebalance-settings">
                    <div class="settings-header">
                        <h3>Otomatik Bakiye Yükleme</h3>
                        <p>Cüzdan bakiyeniz belirlediğiniz eşik değerin altına düştüğünde otomatik olarak bakiye yükler.</p>
                    </div>
                    
                    <div class="settings-content">
                        <div class="setting-row">
                            <div class="setting-label">
                                <label>Otomatik Yükleme</label>
                                <span class="setting-description">Bu özelliği aktif/pasif yapabilirsiniz</span>
                            </div>
                            <div class="setting-control">
                                <a-switch 
                                    v-model="autoRebalanceSettings.enabled" 
                                    @change="onAutoRebalanceEnabledChange"
                                    
                                />
                                <span v-if="autoRebalanceSettings.disabledDueToFailure" class="failure-warning">
                                    Başarısızlık nedeniyle devre dışı bırakıldı
                                </span>
                            </div>
                        </div>

                        <div class="setting-row" v-if="autoRebalanceSettings.enabled">
                            <div class="setting-label">
                                <label>Eşik Değer (TL)</label>
                                <span class="setting-description">Bakiye bu değerin altına düştüğünde otomatik yükleme yapılır</span>
                            </div>
                            <div class="setting-control">
                                <a-input-number 
                                    v-model="autoRebalanceSettings.threshold"
                                    :min="0"
                                    :max="5000"
                                    :step="1"
                                    style="width: 150px"
                                    @change="onSettingsChange"
                                />
                            </div>
                        </div>

                        <div class="setting-row" v-if="autoRebalanceSettings.enabled">
                            <div class="setting-label">
                                <label>Hedef Bakiye (TL)</label>
                                <span class="setting-description">Bakiye bu değere tamamlanacak (eşik değerden büyük olmalı)</span>
                            </div>
                            <div class="setting-control">
                                <a-input-number 
                                    v-model="autoRebalanceSettings.upTo"
                                    :min="autoRebalanceSettings.threshold + 1"
                                    :max="10000"
                                    :step="10"
                                    style="width: 150px"
                                    @change="onSettingsChange"
                                />
                            </div>
                        </div>

                        

                        <div class="setting-row" v-if="autoRebalanceSettings.enabled">
                            <div class="setting-label">
                                <label>Panel Bildirimleri</label>
                                <span class="setting-description">Hangi durumlarda bildirim almak istiyorsunuz</span>
                            </div>
                            <div class="setting-control notification-checkboxes">
                                <a-checkbox 
                                    v-model="autoRebalanceSettings.settings.notifyOnRecharge"
                                    @change="onSettingsChange"
                                >
                                    Başarılı yükleme bildirimi
                                </a-checkbox>
                                <a-checkbox 
                                    v-model="autoRebalanceSettings.settings.notifyOnFailure"
                                    @change="onSettingsChange"
                                >
                                    Başarısızlık bildirimi
                                </a-checkbox>
                                <a-checkbox 
                                    v-model="autoRebalanceSettings.settings.notifyOnDisabled"
                                    @change="onSettingsChange"
                                >
                                    Devre dışı bırakılma bildirimi
                                </a-checkbox>
                            </div>
                        </div>

                        <div class="setting-row" v-if="autoRebalanceSettings.enabled">
                            <div class="setting-label">
                                <label>Email Bildirimleri</label>
                                <span class="setting-description">Kimler email bildirimi alsın (opsiyonel)</span>
                            </div>
                            <div class="setting-control">
                                <a-select
                                    mode="tags"
                                    v-model="autoRebalanceSettings.settings.emailRecipients"
                                    placeholder="Email adreslerini girin (virgül ile ayırın)"
                                    style="width: 300px"
                                    @change="onSettingsChange"
                                    :tokenSeparators="[',', ';', ' ']"
                                >
                                </a-select>
                                <div class="email-hint">
                                    <span class="material-icons">info</span>
                                    Boş bırakırsanız sadece site sahibine email gönderilir
                                </div>
                            </div>
                        </div>

                        <div class="auto-rebalance-status-info" v-if="autoRebalanceSettings.enabled">
                            <div class="status-item">
                                <span class="status-label">Son Tetikleme:</span>
                                <span class="status-value">
                                    {{ autoRebalanceSettings.lastTriggeredAt 
                                        ? formatDate(autoRebalanceSettings.lastTriggeredAt) 
                                        : 'Henüz tetiklenmedi' }}
                                </span>
                            </div>
                            <div class="status-item" v-if="autoRebalanceSettings.failedAttempts > 0">
                                <span class="status-label">Başarısız Deneme:</span>
                                <span class="status-value failure">
                                    {{ autoRebalanceSettings.failedAttempts }}/{{ autoRebalanceSettings.maxFailedAttempts }}
                                </span>
                            </div>
                        </div>

                        <div class="setting-actions">
                            
                            <a-button type="primary" @click="saveAutoRebalanceSettings" :loading="savingSettings">
                                Ayarları Kaydet
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import log2 from "../../../../../utils/log2";
import WalletSVG from "../../components/WalletSVG.vue";
import WalletAddBalance from "../../components/WalletAddBalance.vue";
import CreditCardNeeded from "../../../../../components/billing/CreditCardNeeded.vue";
import AutoRebalancePromoModal from "../../components/AutoRebalancePromoModal.vue";
import moment from "moment";
import MoneyFormatter from "../../../../../components/MoneyFormatter.vue";
import _ from "lodash";
import MiniFilterPreview from "../../../Chatbot/pages/archive/MiniFilterPreview.vue";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import { getAutoRebalanceSettings, updateAutoRebalanceSettings, testAutoRebalance } from "../../../../../api/site";

const log = log2("Settings/WebSiteIntegration");

export default {
    name: "Wallet",
    components: {
        MoneyFormatter,
        WalletSVG,
        WalletAddBalance,
        CreditCardNeeded,
        MiniFilterPreview,
        AutoRebalancePromoModal,
    },
    data() {
        return {
            columns: [
                {
                    title: this?.$t("message.date") || "Tarih",
                    dataIndex: "createdAt",
                    key: "createdAt",
                    scopedSlots: { customRender: "createdAt" },
                },
                {
                    title: this?.$t("message.transactionType") || "İşlem Tipi",
                    dataIndex: "type",
                    key: "type",
                    scopedSlots: { customRender: "type" },
                },
                {
                    title: this?.$t("message.number") || "Numara",
                    dataIndex: "phoneNumber",
                    key: "phoneNumber",
                    scopedSlots: { customRender: "phoneNumber" },
                },
                {
                    title: this?.$t("message.country") || "Ülke",
                    dataIndex: "country",
                    key: "country",
                    scopedSlots: { customRender: "country" },
                },
                {
                    title: this?.$t("message.previousBalance") || "Önceki Bakiye",
                    dataIndex: "before",
                    key: "before",
                    scopedSlots: { customRender: "before" },
                },
                {
                    title: this?.$t("message.nextBalance") || "Sonraki Bakiye",
                    dataIndex: "after",
                    key: "after",
                    scopedSlots: { customRender: "after" },
                },
                {
                    title: this?.$t("message.amount") || "Tutar",
                    dataIndex: "amount",
                    key: "amount",
                    scopedSlots: { customRender: "amount" },
                },
            ],
            addBalanceModal: false,
            creditCardNeededModal: false,
            showAutoRebalancePromo: false,
            activeTab: "all",
            currentPageInput: 1,
            selectedTypes: [],
            autoRebalanceSettings: {
                enabled: false,
                threshold: 0,
                upTo: 100,
                lastTriggeredAt: null,
                failedAttempts: 0,
                lastFailedAt: null,
                maxFailedAttempts: 3,
                disabledDueToFailure: false,
                settings: {
                    cooldownMinutes: 10,
                    notifyOnRecharge: true,
                    notifyOnFailure: true,
                    notifyOnDisabled: true,
                    emailRecipients: []
                }
            },
            savingSettings: false,
            testingAutoRebalance: false,
            settingsChanged: false,
            activeMainTab: "transactions",
            filters: {
                page: 1,
                perPage: 100,
                type: "",
                startDate: null,
                endDate: null,
                channelId: "",
                product: "",
            },
            productTabs: [
                { key: "all", label: "Tümü", icon: "apps" },
                { key: "whatsapp-business-api", label: "WhatsApp", icon: "chat" },
                { key: "ai-usage", label: "AI Kullanımı", icon: "psychology" },
                { key: "voice-agent", label: "Voice Agent", icon: "record_voice_over" },
                { key: "automation", label: "Otomasyon", icon: "settings_applications" },
            ],
            productTypeMapping: {
                "whatsapp-business-api": [
                    "deposit",
                    "credit-usage", 
                    "message",
                    "chat-start",
                    "business-marketing",
                    "business-utility",
                    "business-authentication",
                    "business-service",
                    "free-tier-conversation",
                    "incoming-session-message",
                    "outgoing-session-message",
                    "business-referral-conversion"
                ],
                "voice-agent": [
                    "voice-agent-web-call-outbound",
                    "voice-agent-phone-call-inbound",
                    "voice-agent-phone-call-outbound"
                ],
                "ai-usage": [
                    "ai-usage"
                ],
                "automation": [
                    "cloud-convert",
                    "speech-to-text",
                    "crawler",
                    "google-search",
                    "custom"
                ]
            },
            types: {
                deposit: this?.$t("message.deposit") || "Bakiye Yükleme",
                "credit-usage": this?.$t("message.creditUsage") || "Bakiye Kullanımı",
                "business-marketing": this?.$t("message.businessMarketing") || "Marketing",
                "business-utility": this?.$t("message.businessUtility") || "Utility",
                "business-authentication": this?.$t("message.businessAuthentication") || "OTP",
                "business-service": this?.$t("message.businessService") || "Hizmet",
                "incoming-session-message": this?.$t("message.incomingSessionMessage") || "Gelen Mesaj",
                "outgoing-session-message": this?.$t("message.outgoingSessionMessage") || "Giden Mesaj",
                "free-tier-conversation": this?.$t("message.freeTierConversation") || "Ücretsiz Konuşma (FTC)",
                "chat-start": this?.$t("message.chatStart") || "Sohbet Başlatma",
                message: this?.$t("message.wpMessage") || "Whatsapp Toplu Mesaj",
                "voice-agent-phone-call-inbound": "Gelen Telefon Araması (Voice Agent)",
                "voice-agent-web-call-outbound": "Giden Web Araması (Voice Agent)",
                "voice-agent-phone-call-outbound": "Giden Telefon Araması (Voice Agent)",
                "business-referral-conversion": "Reklam Mesajı",
                "ai-usage": "AI Kullanımı",
                "custom": "Özel Kullanım (Otomasyon)",
                "cloud-convert": "Cloud Convert",
                "speech-to-text": "Speech to Text",
                "crawler": "Crawler",
                "google-search": "Google Arama",
            },
            aiUsageResources: {
                "chatbot-train-website": "Chatbot Website Eğitimi",
                "chatbot-ai-assistant": "Chatbot AI Assistant",
                "recommend-call-function": "Supsis AI Asistanı",
                "response-api": "Response API",
                "chat-completion": "Chat Konusması",
                "automation_javascript": "Automation JavaScript",
                "ask-ai": "Ask AI",
                "ai-bot-response": "AI Bot Yanıtı",
                "ai-classification": "AI Sınıflandırma",
                "ai-random-text": "AI Rastgele Metin",
                "fixGrammar": "Yazım Hatası Düzeltme",
                "rewriteAsLongSentence": "Uzun Cümle Oluşturma",
                "changeTone": "Ton Değiştirme",
                "generateResponse": "Yanıt Oluşturma",
                "ticketAiResponse": "Ticket AI Yanıtı",
                "translation": "Dil Çevirme",
                "promptToGenerateResponse": "Prompt ile Yanıt Oluşturma",
                "mailAiresponse": "Mail AI Yanıtı",
                "mailPromtMessage": "Mail Prompt Mesajı",
                "generateSessionRecommendations": "Sohbet Sonlandırma Sihirbazı",
                "createWorkspace": "Tablo Çalışma Alanı Oluşturma",
                "createTableReports": "Tablo Raporları Oluşturma",
                "createFormula": "Tablo Formülü Oluşturma",
                "table-assistant": "Tablo Yapay Zeka Asistanı",
                "voice-agent": "Sesli Asistan",
                "javascript-generation": "JavaScript Kodu Oluşturma",
                "table-ai-field": "Tablo Yapay Zeka Alanı",
                "summarize": "İçerik Özetleme",
                "sentiment-analysis": "Duygu Analizi",
            },
        };
    },
    computed: {
        ...mapState({
            isAdmin: (state) => state.auth.adminUser !== null,
            wallet: (state) => state.wallet.wallet,
            activities: (state) => state.wallet.activities,
            totalActivity: (state) => state.wallet.totalActivity,
            billingDetail: (state) => state.site.billingDetail,
            settings: (state) => state.setting.settings,
            site: (state) => state.site.site,
            channels: (state) =>
                state.channel.channels.filter((channel) =>
                    ["whatsapp-business-api", "whatsapp-business-api-supsis", "whatsapp-qr", "whatsapp-v2"].includes(channel.type)
                ),
        }),
        totalPages() {
            return Math.ceil(this.totalActivity / this.filters.perPage);
        },
        currentTabTypes() {
            if (this.activeTab === "all") {
                return [];
            }
            return this.productTypeMapping[this.activeTab] || [];
        },
    },
    methods: {
        ...mapActions({
            getBalance: "wallet/getBalance",
            getActivities: "wallet/getWalletLogs",
            addBalance: "wallet/addBalance",
        }),
        getPhoneNumber(item) {
            const numberDetail = _.get(item, "payload.phoneNumber", _.get(item, "payload.to_number"));
            if (!numberDetail) return "-";
            if (typeof numberDetail === "object") {
                return _.get(numberDetail, "number") || "-";
            } else {
                return numberDetail || "-";
            }
        },
        getAIUsageResource(item) {
            const resource = _.get(item, "payload.ref", "-");
            return this.aiUsageResources[resource] || resource;
        },
        formatDate(date) {
            return moment(date).format("YYYY/MM/DD HH:mm:ss");
        },
        formatType(type, item) {
            // Auto rebalance işlemlerini özel olarak göster
            if (type === "deposit" && item?.payload?.autoRebalance) {
                return "Otomatik Bakiye Yükleme";
            }
            return this.types[type] || type;
        },
        seperateWithCommas(x) {
            return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        },
        formatPrice(price) {
            return this.seperateWithCommas(price.toFixed(2));
        },
        openAddWhatsappBalanceModal() {
            if (!this.billingDetail?.binNumber) {
                this.creditCardNeededModal = true;
            } else {
                this.addBalanceModal = true;
            }
        },
        addBalanceWhatsapp(price, invoicePayload) {
            this.addBalance({
                walletType: "whatsapp",
                amount: price,
                invoicePayload,
            })
                .then((result) => {
                    if (result.status === "error") {
                        this.$toasted.error(result.message, {
                            duration: 3000,
                            position: "top-center",
                        });
                        return;
                    }
                    this.addBalanceModal = false;
                    this.$toasted.success(`Whatsapp hesap bakiyenize ${price}TL Bakiye eklenmiştir`, {
                        duration: 3000,
                        position: "top-center",
                    });
                    sendSocketMessage("user wallet-charge", {
                        siteId: this.site._id,
                    });
                    
                    // Manuel yükleme sonrası otomatik yükleme özelliğini tanıt
                    this.checkAndShowAutoRebalancePromo();
                })
                .catch(() => {
                    this.$message.error({
                        content: "Hesabınıza bakiye eklenemedi.",
                        key: "markpaid",
                        duration: 3,
                    });
                });
        },
        checkAndShowAutoRebalancePromo() {
            // localStorage'den kontrol et - daha önce gösterilmiş mi?
            const autoRebalancePromoShown = localStorage.getItem("autoRebalancePromoShown");
            
            // Eğer daha önce gösterilmemişse modalı göster
            // Not: Otomatik yükleme aktif olsa bile, kullanıcı manuel yükleme yapıyorsa 
            // belki otomatik yüklemeyi bilmiyor veya ayarları optimize etmek isteyebilir
            if (!autoRebalancePromoShown) {
                setTimeout(() => {
                    this.showAutoRebalancePromo = true;
                }, 500); // Başarı mesajından sonra göster
            }
        },
        handleAutoRebalancePromoActivate(dontShowAgain) {
            this.showAutoRebalancePromo = false;
            
            // Bir daha gösterme seçeneği işaretliyse localStorage'e kaydet
            if (dontShowAgain) {
                localStorage.setItem("autoRebalancePromoShown", "true");
            }
            
            // Otomatik yükleme sekmesine geç
            this.setActiveMainTab("auto-rebalance");
        },
        handleAutoRebalancePromoClose(dontShowAgain) {
            this.showAutoRebalancePromo = false;
            
            // Bir daha gösterme seçeneği işaretliyse localStorage'e kaydet
            if (dontShowAgain) {
                localStorage.setItem("autoRebalancePromoShown", "true");
            }
        },
        setActiveTab(tabKey) {
            this.activeTab = tabKey;
            this.filters.product = tabKey === "all" ? "" : tabKey;
            
            // Sekme değiştiğinde o sekmenin ilk type'ını seç
            if (tabKey === "all") {
                this.selectedTypes = [];
            } else {
                const tabTypes = this.productTypeMapping[tabKey] || [];
                this.selectedTypes = tabTypes.length > 0 ? [tabTypes[0]] : [];
            }
            
            this.updateTypeFilter();
            this.resetPage();
        },
        toggleType(typeKey, event) {
            const isCtrlPressed = event.ctrlKey || event.metaKey; // metaKey for Mac Cmd key
            const index = this.selectedTypes.indexOf(typeKey);
            
            if (isCtrlPressed) {
                // Ctrl+tık: Multiple seçim modu
                if (index > -1) {
                    this.selectedTypes.splice(index, 1);
                } else {
                    this.selectedTypes.push(typeKey);
                }
            } else {
                // Normal tık: Single seçim modu
                if (index > -1 && this.selectedTypes.length === 1) {
                    // Tek seçili olan chip'e tekrar tıklanırsa, hepsini seç
                    this.selectedTypes = [...this.currentTabTypes];
                } else {
                    // Sadece bu chip'i seç
                    this.selectedTypes = [typeKey];
                }
            }
            
            this.updateTypeFilter();
            this.resetPage();
        },
        updateTypeFilter() {
            if (this.selectedTypes.length === 0) {
                this.filters.type = "";
            } else if (this.selectedTypes.length === 1) {
                this.filters.type = this.selectedTypes[0];
            } else {
                // Birden fazla type seçiliyse comma separated string olarak gönder
                this.filters.type = this.selectedTypes.join(',');
            }
        },
        jumpToPage() {
            if (this.currentPageInput >= 1 && this.currentPageInput <= this.totalPages) {
                this.filters.page = this.currentPageInput;
            } else {
                this.currentPageInput = this.filters.page;
            }
        },
        resetPage() {
            this.filters.page = 1;
            this.currentPageInput = 1;
        },
        getWalletActivities() {
            this.getActivities(this.filters);
        },
        backwardPage() {
            if (this.filters.page <= 1) return;
            this.filters.page = this.filters.page - 1;
            this.currentPageInput = this.filters.page;
        },
        forwardPage() {
            if (Math.ceil(this.totalActivity / this.filters.perPage) <= this.filters.page) return;
            this.filters.page = this.filters.page + 1;
            this.currentPageInput = this.filters.page;
        },
        async loadAutoRebalanceSettings() {
            try {
                const response = await getAutoRebalanceSettings("whatsapp");
                if (response && response.autoRebalance) {
                    this.autoRebalanceSettings = { ...response.autoRebalance };
                }
            } catch (error) {
                console.error("Auto rebalance ayarları yüklenirken hata:", error);
                this.$message.error("Auto rebalance ayarları yüklenemedi");
            }
        },
        async saveAutoRebalanceSettings() {
            this.savingSettings = true;
            try {
                // Frontend validation
                if (this.autoRebalanceSettings.enabled) {
                    if (this.autoRebalanceSettings.threshold < 0) {
                        this.$message.error("Eşik değeri 0 veya daha büyük olmalıdır");
                        this.savingSettings = false;
                        return;
                    }
                    
                    if (this.autoRebalanceSettings.upTo <= 0) {
                        this.$message.error("Hedef bakiye 0'dan büyük olmalıdır");
                        this.savingSettings = false;
                        return;
                    }
                    
                    if (this.autoRebalanceSettings.upTo <= this.autoRebalanceSettings.threshold) {
                        this.$message.error("Hedef bakiye eşik değerden büyük olmalıdır");
                        this.savingSettings = false;
                        return;
                    }
                }

                const payload = {
                    enabled: this.autoRebalanceSettings.enabled,
                    threshold: this.autoRebalanceSettings.threshold,
                    upTo: this.autoRebalanceSettings.upTo,
                    settings: this.autoRebalanceSettings.settings
                };

                console.log("Sending auto rebalance settings:", payload);
                await updateAutoRebalanceSettings("whatsapp", payload);
                
                this.$message.success("Auto rebalance ayarları başarıyla kaydedildi");
                this.settingsChanged = false;
                
                // Ayarları yeniden yükle
                await this.loadAutoRebalanceSettings();
                
            } catch (error) {
                console.error("Auto rebalance ayarları kaydedilirken hata:", error);
                this.$message.error(error.response?.data?.error || "Ayarlar kaydedilemedi");
            } finally {
                this.savingSettings = false;
            }
        },
        async testAutoRebalance() {
            this.testingAutoRebalance = true;
            try {
                const result = await testAutoRebalance("whatsapp");
                
                let message = result.message || "Test tamamlandı";
                let type = "info";
                
                if (result.status === "would_trigger") {
                    type = "warning";
                } else if (result.status === "disabled" || result.status === "disabled_due_to_failure") {
                    type = "error";
                } else if (result.status === "not_needed") {
                    type = "success";
                }
                
                this.$message[type](message);
                
                // Test sonucunu detaylı göster
                if (result.note) {
                    this.$message.info(result.note);
                }
                
            } catch (error) {
                console.error("Auto rebalance test edilirken hata:", error);
                this.$message.error(error.response?.data?.error || "Test başarısız oldu");
            } finally {
                this.testingAutoRebalance = false;
            }
        },
        onAutoRebalanceEnabledChange(enabled) {
            this.settingsChanged = true;
            if (enabled && !this.billingDetail?.binNumber) {
                this.autoRebalanceSettings.enabled = false;
                this.creditCardNeededModal = true;
                this.$message.warning("Otomatik yükleme için önce kredi kartı bilgilerinizi eklemeniz gerekir");
            }
        },
        onSettingsChange() {
            this.settingsChanged = true;
        },
        setActiveMainTab(tab) {
            this.activeMainTab = tab;
        },
        calculateRechargeAmount() {
            if (!this.wallet.whatsapp || !this.autoRebalanceSettings.upTo) {
                return "0.00";
            }
            const amount = this.autoRebalanceSettings.upTo - this.wallet.whatsapp.amount;
            return this.formatPrice(Math.max(0, amount));
        },
    },
    mounted() {
        this.getWalletActivities();
        this.loadAutoRebalanceSettings();
    },
    watch: {
        filters: {
            deep: true,
            handler(newFilters, oldFilters) {
                // Sayfa değişikliği dışındaki filtre değişikliklerinde sayfayı sıfırla
                if (oldFilters && (
                    newFilters.type !== oldFilters.type ||
                    newFilters.channelId !== oldFilters.channelId ||
                    newFilters.startDate !== oldFilters.startDate ||
                    newFilters.endDate !== oldFilters.endDate ||
                    newFilters.product !== oldFilters.product
                )) {
                    this.resetPage();
                }
                this.getWalletActivities();
            },
        },
    },
};
</script>
<style lang="scss">
#wallet-settings {
    .pagination {
        .ant-input-number {
            width: 50px;

            .ant-input-number-handler-wrap {
                display: none;
            }
        }
    }

    .ant-table-body {
        border: 1px solid #e8e8e8;
    }

    .ant-table-tbody > tr:hover:not(.ant-table-expanded-row):not(.ant-table-row-selected) > td {
        background-color: #fff4de !important;
    }

    .ant-table-thead > tr > th {
        background-color: transparent !important;
    }

    tr.ant-table-expanded-row {
        background-color: transparent !important;
    }

    .ant-table-row-expand-icon {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .ant-table-row-expand-icon:focus,
    .ant-table-row-expand-icon:hover {
        color: #ffa800 !important;
        background-color: #fff4de !important;
    }

    .ant-btn:hover,
    .ant-btn:focus {
        color: #ffa800 !important;
        border-color: #ffa800;
    }

    .ant-table-tbody {
        tr > td {
            padding: 3px;
            font-size: smaller;
        }
    }
}
</style>
<style scoped lang="scss">
#wallet-settings {
    .filters-type {
        min-width: 200px;
    }

    .wallet {
        .whatsapp-wallet {
            background-color: #fff4de;
            padding: 20px;
            border-radius: 4px;
            border: 1px solid #ffa800;
            display: flex;
            align-items: center;
            gap: 15px;

            .logo {
                svg {
                    width: 65px;
                    height: 65px;
                }
            }

            .content {
                display: flex;
                align-items: center;
                justify-content: space-between;
                width: 100%;

                .wallet-info {
                    .title {
                        font-size: 22px;
                        font-weight: bold;
                        color: #ffa800;
                    }

                    .balance {
                        font-size: 28px;
                        font-weight: bold;
                        color: #3f3d56;
                    }
                }

                .wallet-add {
                    cursor: pointer;
                    font-size: 16px;
                    background-color: #ffa800;
                    font-weight: bold;
                    color: #3f3d56;
                    padding: 10px 25px;
                    border-radius: 40px;
                    transition: all 0.2s ease;

                    &:hover {
                        background-color: #e6960a;
                    }
                }
            }
        }
    }

    .wallet-content {
        margin-top: 30px;

        .wallet-tabs {
            margin-bottom: 20px;

            .tab-buttons {
                display: flex;
                gap: 2px;
                background-color: #f5f5f5;
                padding: 3px;
                border-radius: 6px;
                width: fit-content;

                .tab-button {
                    padding: 8px 16px;
                    cursor: pointer;
                    border-radius: 4px;
                    font-weight: 500;
                    color: #666;
                    transition: all 0.2s ease;
                    white-space: nowrap;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    gap: 6px;

                    .material-icons {
                        font-size: 18px;
                    }

                    &:hover {
                        background-color: #e6e6e6;
                        color: #333;
                    }

                    &.active {
                        background-color: #ffa800;
                        color: #3f3d56;
                        font-weight: bold;
                    }
                }
            }
        }

        .tab-content {
            min-height: 400px;

            &.auto-rebalance-content {
                .info-alerts {
                    margin-bottom: 24px;
                    display: flex;
                    flex-direction: column;
                    gap: 12px;

                    .alert-item {
                        display: flex;
                        align-items: flex-start;
                        gap: 12px;
                        padding: 16px;
                        border-radius: 8px;
                        border-left: 4px solid;

                        .material-icons {
                            font-size: 20px;
                            margin-top: 2px;
                        }

                        .alert-content {
                            flex: 1;

                            .alert-title {
                                font-weight: 600;
                                font-size: 14px;
                                margin-bottom: 4px;
                            }

                            .alert-text {
                                font-size: 13px;
                                line-height: 1.4;

                                .link {
                                    color: #1890ff;
                                    text-decoration: none;
                                    font-weight: 500;

                                    &:hover {
                                        text-decoration: underline;
                                    }
                                }
                            }
                        }

                        &.warning {
                            background: #fff7e6;
                            border-left-color: #faad14;
                            border: 1px solid #ffe58f;

                            .material-icons {
                                color: #faad14;
                            }

                            .alert-title {
                                color: #d48806;
                            }

                            .alert-text {
                                color: #ad6800;
                            }
                        }

                        &.info {
                            background: #e6f7ff;
                            border-left-color: #1890ff;
                            border: 1px solid #91d5ff;

                            .material-icons {
                                color: #1890ff;
                            }

                            .alert-title {
                                color: #0050b3;
                            }

                            .alert-text {
                                color: #003a8c;
                            }
                        }

                        &.success {
                            background: #f6ffed;
                            border-left-color: #52c41a;
                            border: 1px solid #b7eb8f;

                            .material-icons {
                                color: #52c41a;
                            }

                            .alert-title {
                                color: #389e0d;
                            }

                            .alert-text {
                                color: #237804;
                            }
                        }

                        &.neutral {
                            background: #f5f5f5;
                            border-left-color: #8c8c8c;
                            border: 1px solid #d9d9d9;

                            .material-icons {
                                color: #8c8c8c;
                            }

                            .alert-title {
                                color: #595959;
                            }

                            .alert-text {
                                color: #434343;
                            }
                        }

                        &.compact {
                            .alert-content {
                                .alert-points {
                                    margin-top: 8px;

                                    .point {
                                        display: flex;
                                        align-items: flex-start;
                                        gap: 8px;
                                        margin-bottom: 8px;
                                        font-size: 13px;
                                        line-height: 1.4;

                                        &:last-child {
                                            margin-bottom: 0;
                                        }

                                        .bullet {
                                            font-size: 14px;
                                            flex-shrink: 0;
                                            margin-top: 1px;
                                        }

                                        .link {
                                            color: #1890ff;
                                            text-decoration: none;
                                            font-weight: 500;

                                            &:hover {
                                                text-decoration: underline;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                .auto-rebalance-settings {
                    background: #f9f9f9;
                    border-radius: 8px;
                    padding: 24px;
                    border: 1px solid #e8e8e8;

                    .settings-header {
                        margin-bottom: 24px;

                        h3 {
                            margin: 0 0 8px 0;
                            font-size: 20px;
                            font-weight: 600;
                            color: #3f3d56;
                        }

                        p {
                            margin: 0;
                            color: #666;
                            font-size: 14px;
                        }
                    }

                    .settings-content {
                        .setting-row {
                            display: flex;
                            justify-content: space-between;
                            align-items: flex-start;
                            margin-bottom: 24px;
                            padding-bottom: 24px;
                            border-bottom: 1px solid #e8e8e8;

                            &:last-child {
                                border-bottom: none;
                                margin-bottom: 0;
                                padding-bottom: 0;
                            }

                            .setting-label {
                                flex: 1;
                                margin-right: 24px;

                                label {
                                    display: block;
                                    font-weight: 600;
                                    color: #3f3d56;
                                    margin-bottom: 4px;
                                    font-size: 16px;
                                }

                                .setting-description {
                                    color: #666;
                                    font-size: 14px;
                                    line-height: 1.4;
                                }
                            }

                            .setting-control {
                                display: flex;
                                align-items: center;
                                gap: 12px;

                                .failure-warning {
                                    color: #ff4d4f;
                                    font-size: 12px;
                                    font-weight: 500;
                                }

                                &.notification-checkboxes {
                                    flex-direction: column;
                                    align-items: flex-start;
                                    gap: 8px;
                                }

                                .email-hint {
                                    display: flex;
                                    align-items: center;
                                    gap: 6px;
                                    margin-top: 8px;
                                    font-size: 12px;
                                    color: #999;
                                    font-style: italic;

                                    .material-icons {
                                        font-size: 14px;
                                    }
                                }
                            }
                        }

                        .auto-rebalance-status-info {
                            margin-top: 24px;
                            padding: 16px;
                            background: #fff;
                            border-radius: 6px;
                            border: 1px solid #e8e8e8;

                            .status-item {
                                display: flex;
                                justify-content: space-between;
                                margin-bottom: 8px;

                                &:last-child {
                                    margin-bottom: 0;
                                }

                                .status-label {
                                    font-weight: 500;
                                    color: #666;
                                }

                                .status-value {
                                    color: #3f3d56;

                                    &.failure {
                                        color: #ff4d4f;
                                        font-weight: 500;
                                    }
                                }
                            }
                        }

                        .calculation-preview {
                            margin: 24px 0;
                            padding: 16px;
                            background: #f0f8ff;
                            border: 1px solid #d6e7ff;
                            border-radius: 8px;

                            .preview-header {
                                display: flex;
                                align-items: center;
                                gap: 8px;
                                font-weight: 600;
                                color: #1890ff;
                                margin-bottom: 12px;
                                font-size: 14px;

                                .material-icons {
                                    font-size: 18px;
                                }
                            }

                            .preview-content {
                                .preview-item {
                                    display: flex;
                                    justify-content: space-between;
                                    margin-bottom: 8px;
                                    font-size: 13px;

                                    &:last-child {
                                        margin-bottom: 0;
                                    }

                                    .label {
                                        color: #666;
                                    }

                                    .value {
                                        font-weight: 500;
                                        color: #333;

                                        &.highlight {
                                            color: #1890ff;
                                            font-weight: 600;
                                            font-size: 14px;
                                        }

                                        &.success {
                                            color: #52c41a;
                                            font-weight: 500;
                                        }
                                    }

                                    &.calculation {
                                        padding-top: 8px;
                                        margin-top: 8px;
                                        border-top: 1px solid #d6e7ff;
                                    }
                                }
                            }
                        }

                        .setting-actions {
                            margin-top: 24px;
                            padding-top: 24px;
                            border-top: 1px solid #e8e8e8;
                            display: flex;
                            gap: 12px;
                        }
                    }
                }
            }
        }
    }

    .wallet-logs {

        .product-tabs {
            margin-bottom: 15px;

            .tab-buttons {
                display: flex;
                gap: 2px;
                background-color: #f5f5f5;
                padding: 3px;
                border-radius: 6px;
                width: fit-content;

                .tab-button {
                    padding: 6px 12px;
                    cursor: pointer;
                    border-radius: 4px;
                    font-weight: 500;
                    color: #666;
                    transition: all 0.2s ease;
                    white-space: nowrap;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                    gap: 5px;

                    .material-icons {
                        font-size: 16px;
                    }

                    &:hover {
                        background-color: #e6e6e6;
                        color: #333;
                    }

                    &.active {
                        background-color: #ffa800;
                        color: #3f3d56;
                        font-weight: bold;
                    }
                }
            }

            .type-chips {
                margin-top: 12px;

                .chips-label {
                    font-size: 13px;
                    font-weight: 600;
                    color: #666;
                    margin-bottom: 6px;

                    .chips-hint {
                        font-size: 11px;
                        font-weight: 400;
                        color: #999;
                        font-style: italic;
                    }
                }

                .chips-container {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;

                    .type-chip {
                        padding: 4px 10px;
                        background-color: #f0f0f0;
                        border: 1px solid #d9d9d9;
                        border-radius: 12px;
                        font-size: 11px;
                        cursor: pointer;
                        transition: all 0.2s ease;
                        color: #666;
                        white-space: nowrap;

                        &:hover {
                            background-color: #e6e6e6;
                            border-color: #bfbfbf;
                        }

                        &.active {
                            background-color: #ffa800;
                            border-color: #ffa800;
                            color: #3f3d56;
                            font-weight: 500;
                        }
                    }
                }
            }
        }

        .title {
            font-size: 22px;
            font-weight: bolder;
            color: #3f3d56;
            text-transform: uppercase;
            display: flex;
            align-items: center;
            justify-content: space-between;

            .pagination {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 15px;

                .pagination-controls {
                    display: flex;
                    align-items: center;
                    gap: 15px;

                    .text {
                        font-size: 16px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 5px;
                    }

                    .page-controls {
                        display: flex;
                        align-items: center;
                        gap: 8px;

                        .page-input {
                            width: 60px;
                            text-align: center;
                        }

                        .page-separator {
                            font-size: 14px;
                            color: #666;
                            margin: 0 4px;
                        }

                        .pagination-button {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            width: 32px;
                            height: 32px;

                            .material-icons {
                                font-size: 16px;
                            }

                            &:disabled {
                                opacity: 0.4;
                                cursor: not-allowed;
                            }
                        }
                    }
                }
            }
        }

        .logs {
            margin-top: 20px;
        }
    }
}
</style>
